import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@chakra-ui/react';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import Store from 'lib/Store/withLocalStorage';
import { useController } from 'pages/Meetings/hooks/useController';
import { CardWithLeftStroke, Title } from 'pages/Meetings/pages/Schedule/styles';

import { useWindowSize } from 'hooks/useWindowSize';

import config from 'config/general';
import routes from 'config/routes';

import CardMessage from 'components/atom/CardMessage';
import Button from 'components/atom/Form/Button';
import { Wrapper } from 'components/atom/Grid';
import Loader from 'components/atom/Loader';
import Text from 'components/atom/Text/styles';

import CardMeeting from './components/CardMeeting';
import CardNotice from './components/CardNotice';
import {
  UseControllerAppointmentsProvider,
  useControllerAppointments,
} from './hooks/useControllerAppointments';
import { AppointmentHeader, Container } from './styles';

const authStore = Store(`${config.localStorageKey}-auth`);

const Appointments: React.FC = () => {
  const { t } = useTranslation();
  const { confirmedInvitations, isLoading, havePendingInvitation } = useControllerAppointments();
  const { setPageActive, setInvitationData, setTypeInvitation, setProspectionId, setTab } =
    useController();
  const { width } = useWindowSize();

  const isApp = authStore.getItem('isApp') === 'true';

  const goToPDFGeneratorURL = (): void => {
    const userToken = authStore.getItem(`token`);
    window.location.href = routes.pdfGenerator(
      'user-appointments',
      'BILRJ-2023',
      `&token=${userToken}`,
    );
  };

  return (
    <Container>
      <Wrapper>
        <AppointmentHeader>
          <Title>{!isApp && t('meetings:navigation.appointments')}</Title>

          {confirmedInvitations?.length > 0 && (
            <Button
              text={t('meetings:actions.downloadAppointments')}
              icon={faFilePdf}
              iconPosition="left"
              theme="default-bordered"
              isSmall={width < 768}
              onClick={() => goToPDFGeneratorURL()}
              isDisabled={confirmedInvitations?.length === 0}
            />
          )}
        </AppointmentHeader>

        <Box>
          <Text weight="bold" textTransform="uppercase" padding="0 0 1rem">
            {t('meetings:list.title')}
          </Text>

          {havePendingInvitation && <CardNotice />}

          {isLoading ? (
            <Loader />
          ) : confirmedInvitations?.length > 0 ? (
            <CardWithLeftStroke>
              {confirmedInvitations.map((invitation, i) => (
                <CardMeeting
                  key={i}
                  {...invitation}
                  setInvitation={() => {
                    setPageActive('details');
                    setInvitationData(invitation);
                    setTypeInvitation('invite');
                    setProspectionId(String(invitation.professionalId));
                  }}
                />
              ))}
            </CardWithLeftStroke>
          ) : (
            <>
              <CardMessage message={t('meetings:meeting.emptyMeeting')} setTab={() => setTab(2)} />
            </>
          )}
        </Box>
      </Wrapper>
    </Container>
  );
};

const AppointmentsWrapper: React.FC = () => (
  <UseControllerAppointmentsProvider>
    <Appointments />
  </UseControllerAppointmentsProvider>
);

export default AppointmentsWrapper;
